import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { api, can, type Session } from "../api";
import { Button, Card, ErrorNote, Loading, Pill } from "../components/Ui";
import { useApi } from "../useApi";

interface Candidate {
  spec: string;
  label: string;
  note: string;
  input_per_m: number | null;
  output_per_m: number | null;
}

interface TaskChoice {
  task: string;
  label: string;
  current: string;
  chosen: boolean;
  candidates: Candidate[];
}

interface View {
  tasks: TaskChoice[];
  ai_match_referral: boolean;
  ai_match_chat: boolean;
}

interface TrialResult {
  config: string;
  seconds: number;
  cost_usd: number;
  error: string;
  document_type: string;
  lines: { name: string; price: number; asked_as: string[] }[];
  total: number;
  questions: { asked: string; options: string[] }[];
  unresolved: string[];
}

function huf(amount: number): string {
  return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} Ft`;
}

function labelOf(task: TaskChoice | undefined, spec: string): string {
  return task?.candidates.find((c) => c.spec === spec)?.label ?? spec;
}

/**
 * Feladatonként a mért modellek közül. A chat modellje a Difyban él: mentéskor
 * a szerver ott is átállítja, és ha nem sikerül, a választás sem marad meg.
 */
function Choices({
  view,
  editable,
  onSaved,
}: {
  view: View;
  editable: boolean;
  onSaved: (view: View) => void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async (task: string, spec: string | null): Promise<void> => {
    setBusy(task);
    setError(null);
    try {
      onSaved(await api.put<View>(`/admin/v1/models/${task}`, { spec }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <table className="rows">
        <thead>
          <tr>
            <th>Feladat</th>
            <th>Modell</th>
            <th>Mit tudunk róla (mérés)</th>
          </tr>
        </thead>
        <tbody>
          {view.tasks.map((task) => {
            const current = task.candidates.find((c) => c.spec === task.current);
            return (
              <tr key={task.task}>
                <td>
                  <strong>{task.label}</strong>
                  <div>
                    {task.chosen ? (
                      <Pill tone="ok">az Ön választása</Pill>
                    ) : (
                      <Pill tone="muted">beépített</Pill>
                    )}
                  </div>
                </td>
                <td>
                  <select
                    aria-label={`${task.label} modellje`}
                    value={task.current}
                    disabled={!editable || busy !== null}
                    onChange={(e) => void save(task.task, (e.target as HTMLSelectElement).value)}
                  >
                    {!current && <option value={task.current}>{task.current}</option>}
                    {task.candidates.map((c) => (
                      <option key={c.spec} value={c.spec}>
                        {c.label}
                        {c.input_per_m !== null &&
                          ` — ${c.input_per_m} / ${c.output_per_m} USD per millió token`}
                      </option>
                    ))}
                  </select>
                  {task.chosen && editable && (
                    <Button onClick={() => void save(task.task, null)} disabled={busy !== null}>
                      Vissza a beépítettre
                    </Button>
                  )}
                  {busy === task.task && <span className="muted"> Mentés…</span>}
                </td>
                <td className="muted">{current?.note ?? ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {error && <ErrorNote>{error}</ErrorNote>}
      <p className="muted">
        A mentés után legfeljebb fél perccel minden új kérés az új modellel fut. A Próbapadon
        előtte kipróbálhatja ugyanazt a beutalót vagy kérdést több modellel.
      </p>
    </>
  );
}

function Results({ results }: { results: TrialResult[] }): JSX.Element {
  return (
    <div className="trials">
      {results.map((r) => (
        <section className="card trial" key={r.config}>
          <h3>{r.config}</h3>
          <p className="muted">
            {r.seconds} s · {r.cost_usd.toFixed(4)} USD
            {r.document_type && ` · ${r.document_type}`}
          </p>
          {r.error ? (
            <ErrorNote>{r.error}</ErrorNote>
          ) : (
            <>
              <table className="rows">
                <tbody>
                  {r.lines.map((line) => (
                    <tr key={line.name}>
                      <td>
                        {line.name}
                        {line.asked_as.length > 0 && (
                          <div className="muted">kérve: {line.asked_as.join(", ")}</div>
                        )}
                      </td>
                      <td className="num">{huf(line.price)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <strong>Összesen</strong>
                    </td>
                    <td className="num">
                      <strong>{huf(r.total)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              {r.questions.map((q) => (
                <p key={q.asked}>
                  <Pill tone="warn">kérdés</Pill> „{q.asked}” → {q.options.join(" / ")}
                </p>
              ))}
              {r.unresolved.length > 0 && (
                <p>
                  <Pill tone="bad">egyedi ajánlat</Pill> {r.unresolved.join(", ")}
                </p>
              )}
            </>
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * Próbapad: ugyanaz a beutaló vagy árkérdés legfeljebb három beállítással,
 * egymás mellett. Semmi nem tárolódik, és a próba nem kerül a Feldolgozások
 * közé; a modellhívások ára az Áttekintésben látszik.
 */
function Probapad({ view }: { view: View }): JSX.Element {
  const extractTask = view.tasks.find((t) => t.task === "referral_extract");
  const matchTask = view.tasks.find((t) => t.task === "price_match");
  const pairs = (extractTask?.candidates ?? []).flatMap((e) =>
    (matchTask?.candidates ?? []).map((m) => ({ extract: e.spec, match: m.spec })),
  );
  const [file, setFile] = useState<File | null>(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [matchPicked, setMatchPicked] = useState<string[]>([]);
  const [results, setResults] = useState<TrialResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (list: string[], value: string, set: (v: string[]) => void): void =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value].slice(-3));

  const run = async (kind: "beutalo" | "ar"): Promise<void> => {
    setBusy(true);
    setError(null);
    setResults(null);
    try {
      if (kind === "beutalo" && file) {
        const form = new FormData();
        form.append("file", file);
        for (const key of picked) {
          const [extract, match] = key.split("|");
          form.append("extract", extract ?? "");
          form.append("match", match ?? "");
        }
        setResults(await api.upload<TrialResult[]>("/admin/v1/models/probapad/beutalo", form));
      } else {
        setResults(
          await api.post<TrialResult[]>("/admin/v1/models/probapad/ar", {
            query,
            match: matchPicked,
          }),
        );
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="notice notice--info">
        <p>
          <strong>Csak beteg nélküli tesztlapot töltsön fel</strong> (kitakart név, TAJ, cím) —
          a kép egy modellszolgáltatóhoz megy. Egy beutaló próbája beállításonként néhány
          forintba kerül.
        </p>
      </div>
      <h3>Beutaló</h3>
      <input
        type="file"
        accept="image/*,application/pdf"
        aria-label="Tesztbeutaló"
        onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
      />
      <div className="checks">
        {pairs.map((p) => {
          const key = `${p.extract}|${p.match}`;
          return (
            <label key={key}>
              <input
                type="checkbox"
                checked={picked.includes(key)}
                onChange={() => toggle(picked, key, setPicked)}
              />{" "}
              olvas: {labelOf(extractTask, p.extract)} · párosít: {labelOf(matchTask, p.match)}
            </label>
          );
        })}
      </div>
      <Button onClick={() => void run("beutalo")} disabled={busy || !file || picked.length === 0}>
        {busy ? "Fut…" : "Beutaló próbája"}
      </Button>

      <h3>Árkérdés</h3>
      <input
        type="text"
        className="input"
        aria-label="Árkérdés"
        placeholder="pl. nátrium, OGTT, CRP"
        value={query}
        onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
      />
      <div className="checks">
        {(matchTask?.candidates ?? []).map((c) => (
          <label key={c.spec}>
            <input
              type="checkbox"
              checked={matchPicked.includes(c.spec)}
              onChange={() => toggle(matchPicked, c.spec, setMatchPicked)}
            />{" "}
            {c.label}
          </label>
        ))}
      </div>
      <Button
        onClick={() => void run("ar")}
        disabled={busy || !query.trim() || matchPicked.length === 0}
      >
        {busy ? "Fut…" : "Árkérdés próbája"}
      </Button>

      {error && <ErrorNote>{error}</ErrorNote>}
      {results && <Results results={results} />}
    </>
  );
}

export function ModelsView({ session }: { session: Session }): JSX.Element {
  const { data, error, loading } = useApi<View>("/admin/v1/models");
  const [saved, setSaved] = useState<View | null>(null);
  const view = saved ?? data;
  const editable = can(session, "beallit");

  if (loading && !view) return <Loading />;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!view) return <Loading />;
  return (
    <>
      <Card
        title="Modellek"
        hint="Melyik mesterséges intelligencia végzi az egyes feladatokat. Az árat mindig a program számolja az árlistából."
      >
        <Choices view={view} editable={editable} onSaved={setSaved} />
      </Card>
      {editable && (
        <Card title="Próbapad" hint="Ugyanaz a beutaló vagy kérdés több modellel, egymás mellett">
          <Probapad view={view} />
        </Card>
      )}
    </>
  );
}
